import {I18n} from '@aws-amplify/core';
import {Text} from '@mantine/core';
import {modals} from '@mantine/modals';
import {AiOutlineDelete} from 'react-icons/ai';

import {FlexiButton} from '@flexinet/ui-components';

import {useDeleteAccountMutation} from '../hooks/useAccountsQuery';
import {useAccountsSelection} from '../hooks/useAccountsSelection.ts';


// FNP-377: the admin Remove button invokes the canonical deleteAccount
// soft-delete pipeline (same path portal users hit), so the row actually
// flips to PENDING_DELETION + emits the workflows-api event. Previously
// wired to removeAccount, which only emitted the event without touching DDB.
//
// FNP-956: this deliberately does NOT use DeleteRecordsConfirmationButton from
// @flexinet/ui-components, even though 14 other modules do. That component's
// copy says "delete", and its trigger reads "Delete N Selected" — both wrong
// here. Remove is a *soft* delete: reversible for 30 days via the Restore
// button sitting immediately beside it, so telling the operator they are
// deleting would be inaccurate, and a "Delete" button next to a "Restore"
// button is a contradictory affordance.
//
// The rule this follows: the library owns the AFFORDANCE (modal chrome, danger
// styling, cancel/confirm); the feature owns the CONSEQUENCE (what state the
// record moves to, whether it is reversible, for how long). A sentence that
// would be false if pasted into another module belongs in the module.
// Precedent: organization-accounts/buttons/DeactivateRecordButton.tsx.
// Do not "fix" this back to the library component.
export const RemoveRecordButton = () => {

    const deleteAccountMutation = useDeleteAccountMutation();

    const {
        selection,
        handleClearSelection,
    } = useAccountsSelection();

    const recordIds = Object.keys(selection).filter(id => selection[id] === true);
    const disabled = recordIds.length === 0;

    // FNP-956: each removal is attempted independently. Previously a rejection
    // on item 3 of 5 aborted the loop AND skipped handleClearSelection(), so
    // the operator was left with a half-applied cascade and a stale selection
    // still ticked. The mutation's onError already raises a notification per
    // failure, so swallowing the rejection here loses no signal — it only stops
    // one failure from cancelling the remaining removals the operator confirmed.
    const handleRemove = async () => {
        for (const id of recordIds) {
            try {
                await deleteAccountMutation.mutateAsync(id);
            } catch {
                // notified by the mutation's onError; continue with the rest.
            }
        }
        handleClearSelection();
    };

    // The count goes in the label as well as the modal: giving up the library
    // component means giving up its built-in "Delete N Selected" interpolation,
    // so it is re-implemented here rather than lost.
    const label = disabled
        ? I18n.get('Remove')
        : `${I18n.get('Remove')} (${recordIds.length})`;

    const openConfirmModal = () =>
        modals.openConfirmModal({
            title: I18n.get('Remove selected accounts'),
            children: (
                <Text size='sm'>
                    {recordIds.length === 1
                        ? I18n.get('This account will be deactivated and scheduled for deletion.')
                        : `${recordIds.length} ${I18n.get('accounts will be deactivated and scheduled for deletion.')}`}
                    {' '}
                    {I18n.get('You can restore them for 30 days, after which they are permanently removed.')}
                </Text>
            ),
            labels: {
                confirm: I18n.get('Remove'),
                cancel: I18n.get('Cancel'),
            },
            confirmProps: {color: 'red'},
            onConfirm: () => {
                void handleRemove();
            },
        });

    return (<FlexiButton
        leftSection={<AiOutlineDelete/>}
        disabled={disabled}
        onClick={openConfirmModal}
    >{label}</FlexiButton>);

};
